import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  bindingListSchema,
  mailboxListSchema,
  monitoringCheckPageSchema,
  monitoringCheckResultPageSchema,
  manualMetricEntrySchema,
  serviceCreateSchema,
  serviceMetricSchema,
  serviceMetricsSchema,
  serverDisksSchema,
  backupStatusSchema,
  servicePageSchema,
  serviceSchema,
  type Binding,
  type MonitoringCheck,
  type MonitoringCheckResult,
  type Service,
  type ServiceCreate,
} from '@bekebi/contracts/api';
import { apiRequest } from '../../api/client';
import { isUuid } from '../../api/ids';

/**
 * Data access for the Admin Services area — thin wrappers over existing endpoints, validated
 * against packages/contracts. Nothing here computes health or any other state: every value shown
 * comes from the API as returned.
 */

export const serviceKeys = {
  all: ['services'] as const,
  list: () => [...serviceKeys.all, 'list'] as const,
  detail: (id: string) => [...serviceKeys.all, 'detail', id] as const,
  bindings: (id: string) => [...serviceKeys.all, 'bindings', id] as const,
  checks: (id: string) => [...serviceKeys.all, 'checks', id] as const,
  latestResult: (checkId: string) => [...serviceKeys.all, 'latest-result', checkId] as const,
  mailboxStatus: () => ['mailbox-status'] as const,
  metrics: (id: string) => [...serviceKeys.all, 'metrics', id] as const,
  disks: (id: string) => [...serviceKeys.all, 'disks', id] as const,
  backups: () => ['backups'] as const,
};

/** Ids from the URL are only sent to the API when well-formed (shared rule, api/ids.ts). */
export const isServiceId = isUuid;

/** Every service, following the API's cursor pagination (bounded). */
async function fetchAllServices(): Promise<Service[]> {
  const items: Service[] = [];
  let cursor: string | null = null;
  for (let page = 0; page < 20; page += 1) {
    const query: string = cursor ? `?limit=100&cursor=${encodeURIComponent(cursor)}` : '?limit=100';
    const result = await apiRequest(`/services${query}`, { schema: servicePageSchema });
    items.push(...result.items);
    cursor = result.nextCursor;
    if (!cursor) break;
  }
  return items;
}

export function useServices() {
  return useQuery({ queryKey: serviceKeys.list(), queryFn: fetchAllServices });
}

/** The latest metric values of one service, as stored by the collectors (values only). */
export function useServiceMetrics(id: string, enabled = true) {
  return useQuery({
    queryKey: serviceKeys.metrics(id),
    queryFn: () => apiRequest(`/services/${id}/metrics`, { schema: serviceMetricsSchema }),
    enabled,
  });
}

/** Mail Server backup status, from the backup objects seen in the Storage Bucket (A-M6). */
export function useBackupStatus() {
  return useQuery({
    queryKey: serviceKeys.backups(),
    queryFn: () => apiRequest('/backups', { schema: backupStatusSchema }),
  });
}

/** The Cloud Server's disks, latest reading over SSH (Admin only — enable for monitoring.view). */
export function useServerDisks(id: string, enabled: boolean) {
  return useQuery({
    queryKey: serviceKeys.disks(id),
    queryFn: () => apiRequest(`/services/${id}/disks`, { schema: serverDisksSchema }),
    enabled,
  });
}

export function useService(id: string) {
  return useQuery({
    queryKey: serviceKeys.detail(id),
    queryFn: () => apiRequest(`/services/${id}`, { schema: serviceSchema }),
  });
}

/** Infrastructure bindings of a service (`infrastructure.view`). */
export function useServiceBindings(id: string, enabled: boolean) {
  return useQuery({
    queryKey: serviceKeys.bindings(id),
    queryFn: async (): Promise<Binding[]> =>
      (await apiRequest(`/services/${id}/bindings`, { schema: bindingListSchema })).items,
    enabled,
  });
}

/** Configured monitoring checks of a service (`monitoring.view`), read-only here. */
export function useServiceChecks(id: string, enabled: boolean) {
  return useQuery({
    queryKey: serviceKeys.checks(id),
    queryFn: async (): Promise<MonitoringCheck[]> =>
      (
        await apiRequest(`/monitoring/checks?serviceId=${id}&limit=100`, {
          schema: monitoringCheckPageSchema,
        })
      ).items,
    enabled,
  });
}

/** The newest stored result of one check (for its fixed error code and message). */
export function useLatestCheckResult(checkId: string, enabled: boolean) {
  return useQuery({
    queryKey: serviceKeys.latestResult(checkId),
    queryFn: async (): Promise<MonitoringCheckResult | null> =>
      (
        await apiRequest(`/monitoring/checks/${checkId}/results?limit=1`, {
          schema: monitoringCheckResultPageSchema,
        })
      ).items[0] ?? null,
    enabled,
  });
}

/**
 * Whether the Mail Server connector is available, read from the mailbox endpoint's own answer,
 * and — when it is — a summary of what the Mail Server reported (no figure is invented).
 */
export type MailboxCapability = 'not_connected' | 'available' | 'unavailable';

export interface MailboxStatus {
  capability: MailboxCapability;
  /** Only when available: how many mailboxes the Mail Server reported. */
  count: number | null;
  /** Only when available: total space used by the mailboxes whose usage the Mail Server reported. */
  usedBytes: number | null;
  /** How many mailboxes had no usage reported (their usage is not in `usedBytes`). */
  usageUnreported: number;
}

/** What each unavailable answer means, in the same words wherever it is shown. */
export const MAILBOX_CAPABILITY_TEXT: Record<Exclude<MailboxCapability, 'available'>, string> = {
  not_connected: 'Mailbox management is not connected yet.',
  unavailable: 'Mailbox management is unavailable right now.',
};

/** e.g. "14 mailboxes on the Mail Server, 6.2 GB used." — only from reported values. */
export function mailboxSummaryText(status: MailboxStatus): string {
  if (status.capability !== 'available') return MAILBOX_CAPABILITY_TEXT[status.capability];
  const count = status.count ?? 0;
  const noun = count === 1 ? 'mailbox' : 'mailboxes';
  if (count === 0) return 'The Mail Server reports no mailboxes.';
  const used =
    status.usedBytes === null || status.usageUnreported === count
      ? ''
      : `, ${formatDecimalBytes(status.usedBytes)} used${status.usageUnreported > 0 ? ' (usage not reported for some)' : ''}`;
  return `${count} ${noun} on the Mail Server${used}.`;
}

function formatDecimalBytes(bytes: number): string {
  const units = ['bytes', 'KB', 'MB', 'GB', 'TB'];
  let value = bytes;
  let unit = 0;
  while (value >= 1000 && unit < units.length - 1) {
    value /= 1000;
    unit++;
  }
  return unit === 0 ? `${value} bytes` : `${value.toFixed(1)} ${units[unit]}`;
}

export function useMailboxCapability(enabled: boolean) {
  return useQuery({
    queryKey: serviceKeys.mailboxStatus(),
    queryFn: async (): Promise<MailboxStatus> => {
      try {
        const { items } = await apiRequest('/mail/mailboxes', { schema: mailboxListSchema });
        const reported = items.filter((m) => m.usedBytes !== null);
        return {
          capability: 'available',
          count: items.length,
          usedBytes: reported.reduce((sum, m) => sum + (m.usedBytes ?? 0), 0),
          usageUnreported: items.length - reported.length,
        };
      } catch (error) {
        const code = (error as { code?: string }).code;
        if ((error as { status?: number }).status === 401) throw error;
        const capability = code === 'mail_server_not_connected' ? 'not_connected' : 'unavailable';
        return { capability, count: null, usedBytes: null, usageUnreported: 0 };
      }
    },
    enabled,
    retry: false,
  });
}

export function useCreateService() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ServiceCreate) =>
      apiRequest('/services', {
        method: 'POST',
        body: serviceCreateSchema.parse(input),
        schema: serviceSchema,
      }),
    onSuccess: async (service) => {
      queryClient.setQueryData(serviceKeys.detail(service.id), service);
      await queryClient.invalidateQueries({ queryKey: serviceKeys.list() });
    },
  });
}

/**
 * Changes the Website's public address (PATCH /services/:id, `services.manage`). The API resets
 * the Website check in the same transaction, so everything about this service is re-read from the
 * API afterwards — nothing is updated locally.
 */
export function useUpdateWebsiteAddress(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (accessUrl: string | null) =>
      apiRequest(`/services/${id}`, {
        method: 'PATCH',
        body: { accessUrl },
        schema: serviceSchema,
      }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: serviceKeys.detail(id) }),
        queryClient.invalidateQueries({ queryKey: serviceKeys.list() }),
        queryClient.invalidateQueries({ queryKey: serviceKeys.checks(id) }),
        queryClient.invalidateQueries({ queryKey: [...serviceKeys.all, 'latest-result'] }),
      ]);
    },
  });
}

/**
 * Enters the Storage Bucket's plan size by hand (POST /services/:id/metrics, `services.manage`):
 * the size shown on the storage service's own dashboard. The figures are re-read from the API.
 */
export function useEnterPlanSize(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (bytes: number) =>
      apiRequest(`/services/${id}/metrics`, {
        method: 'POST',
        body: manualMetricEntrySchema.parse({ key: 'storage.bucket.capacity_bytes', value: bytes }),
        schema: serviceMetricSchema,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: serviceKeys.metrics(id) });
    },
  });
}
